'use client';
import { Icon } from '@core/icons';
import { Button } from '@core/ui/components/atoms/button';
import Link from 'next/link';
import { useEffect } from 'react';
import { boldTheme } from '#src/errors/themes/bold';
import { corporateTheme } from '#src/errors/themes/corporate';
import { minimalTheme } from '#src/errors/themes/minimal';
import { logApplicationError } from '#src/errors/utils/error-logger';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
function getThemeConfig(theme = "corporate") {
  if (theme === "minimal") {
    return minimalTheme;
  }
  if (theme === "bold") {
    return boldTheme;
  }
  return corporateTheme;
}
__name(getThemeConfig, "getThemeConfig");
function ErrorBoundaryTemplate({
  error,
  reset,
  theme = "corporate",
  appName,
  title = "Something went wrong",
  message = "An unexpected error occurred. Please try again or return to the hom\
epage.",
  actions = [
    { label: "Try Again", onClick: /* @__PURE__ */ __name(() => reset(), "onClic\
k"), variant: "default" },
    { label: "Go Home", href: "/", variant: "outline" }
  ],
  showErrorDetails = false,
  enableLogging = true,
  logContext
}) {
  const themeConfig = getThemeConfig(theme);
  useEffect(() => {
    if (enableLogging) {
      logApplicationError({
        error,
        digest: error.digest,
        appName,
        customContext: logContext
      });
    }
  }, [error, appName, enableLogging, logContext]);
  return /* @__PURE__ */ jsx("div", { className: "flex min-h-screen items-center\
 justify-center px-4 py-16", children: /* @__PURE__ */ jsxs("div", { className: "\
flex flex-col items-center gap-6 text-center max-w-md", "data-theme": theme, children: [
    /* @__PURE__ */ jsx("div", { className: "rounded-full bg-destructive/10 p-4",
    children: /* @__PURE__ */ jsx(
      Icon,
      {
        type: "lucide",
        icon: "AlertCircle",
        className: `${themeConfig.iconSize} text-destructive`
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "rounded-full border border-destruct\
ive/20 bg-destructive/5 px-4 py-1.5", children: /* @__PURE__ */ jsx("span", { className: "\
text-sm font-medium text-destructive", children: "500" }) }),
    /* @__PURE__ */ jsx("h1", { className: `${themeConfig.headingSize} font-bold\
 tracking-tight text-foreground`, children: title }),
    /* @__PURE__ */ jsx("p", { className: "text-base text-muted-foreground", children: message }),
    showErrorDetails && /* @__PURE__ */ jsxs("div", { className: "w-full rounded\
-lg bg-muted/50 p-4 text-left border border-border/40", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs font-mono text-muted-foregr\
ound break-all", children: error.message }),
      error.digest && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted\
-foreground mt-2", children: [
        "Error ID: ",
        /* @__PURE__ */ jsx("code", { className: "font-mono", children: error.digest })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col sm:flex-row gap-3 w-f\
ull sm:w-auto mt-4", children: actions.map((action, index) => {
      const ButtonContent = /* @__PURE__ */ jsxs(Fragment, { children: [
        action.label === "Try Again" && /* @__PURE__ */ jsx(Icon, { type: "lucid\
e", icon: "RotateCw", className: "mr-2 h-4 w-4" }),
        action.label === "Go Home" && /* @__PURE__ */ jsx(Icon, { type: "lucide",
        icon: "Home", className: "mr-2 h-4 w-4" }),
        action.label.toLowerCase().includes("help") && action.label.toLowerCase().
        includes("support") && /* @__PURE__ */ jsx(Icon, { type: "lucide", icon: "\
HelpCircle", className: "mr-2 h-4 w-4" }),
        action.label
      ] });
      const key = `${action.label}-${action.href ?? "action"}-${index}`;
      if (action.href) {
        return /* @__PURE__ */ jsx(Button, { variant: action.variant ?? "default",
        asChild: true, children: /* @__PURE__ */ jsx(Link, { href: action.href, children: ButtonContent }) },
        key);
      }
      return /* @__PURE__ */ jsx(Button, { variant: action.variant ?? "default",
      onClick: action.onClick, children: ButtonContent }, key);
    }) })
  ] }) });
}
__name(ErrorBoundaryTemplate, "ErrorBoundaryTemplate");

export { ErrorBoundaryTemplate };
